<script setup lang="ts">
import useEmblaCarousel from 'embla-carousel-vue'
import { ref, useTemplateRef, onMounted } from 'vue'
import { getReviews } from '~/composables/reviews'
import type { CmsCustomComponentType } from '~/layers/cms/app/types/cmsTypes'
import type { PlaceReviewsResponse } from '~/types/types'

const OPTIONS = { dragFree: true, loop: false }
const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS)

const isUserDragging = ref(false)

const emblaBtnPrev = useTemplateRef<HTMLElement>('emblaBtnPrev')
const emblaBtnNext = useTemplateRef<HTMLElement>('emblaBtnNext')

const opinie = ref<PlaceReviewsResponse>()

const refreshOpinie = async () => {
  const res = await getReviews()
  if (res.status_ok) {
    opinie.value = res.data as PlaceReviewsResponse
  }
}

onMounted(() => {
  refreshOpinie()

  if (!emblaApi.value) return

  emblaBtnNext.value?.addEventListener('click', () => {
    emblaApi.value?.scrollNext()
  })

  emblaBtnPrev.value?.addEventListener('click', () => {
    emblaApi.value?.scrollPrev()
  })

  emblaApi.value.on('pointerDown', () => {
    isUserDragging.value = true
  })

  emblaApi.value.on('pointerUp', () => {
    isUserDragging.value = false
  })
})

const props = defineProps<{
  cmsDataPath: (string | number)[]
}>()
const nazwa_komponentu = 'Sliders/Opinie'
const component_default_values: CmsCustomComponentType = {
  id: { value: undefined, default_value: 'Lorem Ipsum', settingType: 'text', setting_label: 'zmien id' }
}
</script>

<template>
  <CmsCustomComponent
    v-slot="{ component_values }"
    :cms-data-path="[...props.cmsDataPath]"
    :component-name="nazwa_komponentu"
    :defaults="component_default_values"
    :open-options="true"
  >
    <div
      :id="component_values?.id?.value"
      class="py-10 xl:py-20 pl-2xl flex flex-col gap-20"
    >
      <div class="flex flex-col gap-7.5">
        <span class="text-H2 text-Tekst">
          <CmsText :cms-data-path="[...props.cmsDataPath, 'component_data', 'text_1']" />
        </span>
        <span class="text-H4 text-Tekst">
          <CmsText :cms-data-path="[...props.cmsDataPath, 'component_data', 'text_2']" />
        </span>
      </div>

      <div class="flex flex-col gap-6">
        <div class="embla">
          <div
            ref="emblaRef"
            class="overflow-hidden"
          >
            <div
              class="flex gap-8 select-none"
            >
              <div
                v-for="(opinia, index) in opinie?.reviews"
                :key="index"
                class="embla__slide basis-6/10 xl:basis-15/35 shrink-0 h-full"
              >
                <div class="bg-background-2 rounded-3xl p-5 flex flex-col gap-5">
                  <NuxtImg
                    src="https://api.ostoja-seniora.pl/files/quotation.svg"
                    alt="quotation"
                    width="25"
                    height="18"
                    quality="100"
                    loading="lazy"
                  />
                  <div class="flex flex-col gap-7.5">
                    <span class="text-Paragraph text-Tekst">
                      {{ opinia.originalText.text }}
                    </span>
                    <span class="text-Paragraph text-Tekst">
                      <strong>{{ opinia.authorAttribution.displayName }}</strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-start">
          <div class="flex flex-row ">
            <div
              ref="emblaBtnPrev"
              class="px-7.5 py-5 border border-r-0 border-Tekst rounded-l-full transition-all duration-200 group group-hover:border-Secondary cursor-pointer"
            >
              <i class="pi pi-arrow-left text-Tekst group-hover:text-Secondary text-2xl! transition-all duration-200" />
            </div>
            <div
              ref="emblaBtnNext"
              class="px-7.5 py-5 border border-l-0 border-Tekst rounded-r-full transition-all duration-200 group group-hover:border-Secondary cursor-pointer"
            >
              <i class="pi pi-arrow-right text-Tekst group-hover:text-Secondary text-2xl! transition-all duration-200" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </CmsCustomComponent>
</template>
